import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as BABYLON from '@babylonjs/core'

export interface OverlayRegistration {
  getWorldPos: () => BABYLON.Vector3 | null
  /** Overlay stays mounted but hidden when its world point is behind the camera or off-frustum. Default true. */
  hideWhenBehind?: boolean
  /** Skip visibility entirely — always show at the projected point (e.g. a live presence cursor). */
  always?: boolean
}

export interface UseOverlayProjectionApi {
  register: (id: string, el: HTMLElement, reg: OverlayRegistration) => void
  unregister: (id: string) => void
}

/**
 * Projects registered 3D points into screen-space CSS coordinates every
 * frame and writes directly to each element's `style` — same technique as
 * the prototype's `project()`/overlay loop. Deliberately bypasses React
 * state for the per-frame write (60fps setState would thrash the tree);
 * React only owns mount/unmount of the overlay DOM nodes themselves.
 */
export function useOverlayProjection(
  scene: BABYLON.Scene | null,
  containerRef: React.RefObject<HTMLElement>,
): UseOverlayProjectionApi {
  const entriesRef = useRef<Map<string, { el: HTMLElement; reg: OverlayRegistration }>>(new Map())

  const register = useCallback((id: string, el: HTMLElement, reg: OverlayRegistration) => {
    entriesRef.current.set(id, { el, reg })
  }, [])
  const unregister = useCallback((id: string) => {
    entriesRef.current.delete(id)
  }, [])

  useEffect(() => {
    if (!scene) return
    const engine = scene.getEngine()

    const observer = scene.onBeforeRenderObservable.add(() => {
      const container = containerRef.current
      const camera = scene.activeCamera
      if (!container || !camera) return
      const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight())
      const scaleX = container.clientWidth / engine.getRenderWidth()
      const scaleY = container.clientHeight / engine.getRenderHeight()

      entriesRef.current.forEach(({ el, reg }) => {
        const worldPos = reg.getWorldPos()
        if (!worldPos) {
          el.style.opacity = '0'
          el.style.pointerEvents = 'none'
          return
        }
        const projected = BABYLON.Vector3.Project(worldPos, BABYLON.Matrix.Identity(), scene.getTransformMatrix(), viewport)
        const behind = projected.z < 0 || projected.z > 1
        el.style.left = `${(projected.x * scaleX).toFixed(1)}px`
        el.style.top = `${(projected.y * scaleY).toFixed(1)}px`
        if (reg.always) {
          el.style.opacity = '1'
          el.style.pointerEvents = 'auto'
        } else if (behind && (reg.hideWhenBehind ?? true)) {
          el.style.opacity = '0'
          el.style.pointerEvents = 'none'
        } else {
          el.style.opacity = '1'
          el.style.pointerEvents = 'auto'
        }
      })
    })

    return () => {
      scene.onBeforeRenderObservable.remove(observer)
    }
  }, [scene, containerRef])

  return useMemo(() => ({ register, unregister }), [register, unregister])
}
